import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useMemo, useState } from 'react';
import {
  useAuthUserRestaurant,
  useEditSupplierMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { SupplierWithEmails } from '@durma-soft/gros-sdk/dist/types/suppliers';

import { Modal } from '@/components/shared/modal';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import {
  SupplierFormData,
  SupplierFormFields,
  isSupplierFormValid,
} from '@/components/restaurant/suppliers/supplier-form-fields';

interface EditSupplierModalProps {
  supplier: SupplierWithEmails;
  onClose: () => unknown;
}

export const EditSupplierModal = ({
  supplier,
  onClose,
}: EditSupplierModalProps) => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [formData, setFormData] = useState<SupplierFormData>({
    name: supplier.name,
    emails: supplier.emails.length
      ? supplier.emails.map((email) => email.email)
      : [''],
  });

  const editSupplier = useEditSupplierMutation();

  const isValid = useMemo(() => isSupplierFormValid(formData), [formData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await editSupplier.mutateAsync({
        restaurant_id: restaurant.id,
        supplier_id: supplier.id,
        name: formData.name,
        emails: formData.emails,
      });
      onClose();
      toast.success(t('suppliers.edit-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('suppliers.edit-error-msg'));
    }
  };

  return (
    <Modal
      isModalOpen
      closeModal={onClose}
      title={t('suppliers.edit')}
      description={t('suppliers.edit-details')}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <SupplierFormFields formData={formData} setFormData={setFormData} />
        <DialogFooter className="mt-2">
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={editSupplier.isPending}
          >
            {t('suppliers.edit')}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
